import React from 'react'
import PropTypes from 'prop-types'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'

const transformHourlySessionsData = (data, valueKey = 'sessions', isAnalyticsOverview = false) => {
  // If the server supplied hourlySessions/trends, prefer that explicit data
  if (data && data.hourlySessions && Array.isArray(data.hourlySessions)) {
    return data.hourlySessions
      .map(session => ({
        time: formatHourForChart(session.hour),
        count: session[valueKey] || 0,
        activeSessions: session.activeSessions || 0,
        uniqueUsers: session.uniqueUsers || 0,
        deviceBreakdown: session.deviceBreakdown || [],
        originalHour: session.hour
      }))
      .filter(session => session.time !== null)
  }
  
  if (data && data.sessions && Array.isArray(data.sessions)) {
    return data.sessions
      .map(session => {
        const hour = session.createdAt ? new Date(session.createdAt).getHours() : null
        const formattedTime = hour !== null ? formatHourForChart(`${hour}:00`) : null
        
        if (formattedTime === null) return null
        
        return {
          time: formattedTime,
          count: 1, 
          activeSessions: session.isActive ? 1 : 0,
          uniqueUsers: 1,
          deviceBreakdown: [session.deviceType] || [],
          originalHour: `${hour}:00`,
          browser: session.browser,
          deviceType: session.deviceType,
          os: session.os
        }
      })
      .filter(session => session !== null)
      .reduce((acc, session) => {
        const existingIndex = acc.findIndex(item => item.time === session.time)
        if (existingIndex >= 0) {
          acc[existingIndex].count += session.count
          acc[existingIndex].activeSessions += session.activeSessions
          acc[existingIndex].uniqueUsers += session.uniqueUsers
        } else {
          acc.push(session)
        }
        return acc
      }, [])
      .sort((a, b) => {
        const timeA = parseTimeForSorting(a.time)
        const timeB = parseTimeForSorting(b.time)
        return timeA - timeB
      })
  }
  
  if (data && typeof data.messagesCount === 'number' && !isAnalyticsOverview) {
    const totalMessages = data.messagesCount
    const businessHours = [
      { time: '7 AM', percentage: 0.05 },   
      { time: '8 AM', percentage: 0.12 },   
      { time: '9 AM', percentage: 0.15 },   
      { time: '10 AM', percentage: 0.18 },  
      { time: '11 AM', percentage: 0.16 },  
      { time: '12 PM', percentage: 0.10 },  
      { time: '1 PM', percentage: 0.08 },   
      { time: '2 PM', percentage: 0.06 },   
      { time: '3 PM', percentage: 0.05 },   // 5% - mid afternoon
      { time: '4 PM', percentage: 0.03 },   // 3% - late afternoon
      { time: '5 PM', percentage: 0.02 },   // 2% - end of day
    ]
    
    return businessHours.map(hour => ({
      time: hour.time,
      count: Math.round(totalMessages * hour.percentage),
      activeSessions: 0,
      uniqueUsers: 0,
      deviceBreakdown: [],
      originalHour: hour.time
    }))
  }

  // If it's the analytics overview and we only have a total messages count (no hourlySessions), create a simple hourly distribution
  if (isAnalyticsOverview && data && typeof data.messagesCount === 'number') {
    const totalMessages = data.messagesCount
    const hourlyDistribution = [
      { time: '12:00', percentage: 0.01 },
      { time: '13:00', percentage: 0.08 },
      { time: '14:00', percentage: 0.50 },
      { time: '15:00', percentage: 0.40 },
      { time: '16:00', percentage: 0.58 },
      { time: '17:00', percentage: 0.48 },
      { time: '18:00', percentage: 0.01 },
    ]
    return hourlyDistribution.map(hour => ({
      time: hour.time,
      count: Math.round(totalMessages * hour.percentage),
      activeSessions: 0,
      uniqueUsers: 0,
      deviceBreakdown: [],
      originalHour: hour.time,
    }))
  }
  
  if (Array.isArray(data)) {
    return data
      .map(session => {
        if (session.time && session.count !== undefined) {
          const hourCheck = session.time
          const timeLabels = ['7 AM', '8 AM', '9 AM', '10 AM', '11 AM', '12 PM', '1 PM', '2 PM', '3 PM', '4 PM', '5 PM']
          if (timeLabels.includes(hourCheck)) {
            return session
          }
          return null
        }
        
        const formattedTime = session.hour ? formatHourForChart(session.hour) : session.time
        if (formattedTime === null) return null
        
        return {
          time: formattedTime,
          count: session[valueKey] || session.count || 0,
          activeSessions: session.activeSessions || 0,
          uniqueUsers: session.uniqueUsers || 0,
          deviceBreakdown: session.deviceBreakdown || [],
          originalHour: session.hour
        }
      })
      .filter(session => session !== null)
  }
  
  return []
}

const parseTimeForSorting = (timeStr) => {
  const [time, period] = timeStr.split(' ')
  let [hour] = time.split(':')
  hour = parseInt(hour, 10)
  
  if (period === 'PM' && hour !== 12) {
    hour += 12
  } else if (period === 'AM' && hour === 12) {
    hour = 0
  }
  
  return hour
}

const formatHourForChart = (hour) => {
  if (!hour) return ''
  
  const [hourStr] = hour.split(':')
  const hourNum = parseInt(hourStr, 10)
  
  if (hourNum === 0) return '12 AM'
  if (hourNum < 7) return null
  if (hourNum >= 7 && hourNum < 12) return `${hourNum} AM`
  if (hourNum === 12) return '12 PM'
  if (hourNum > 12 && hourNum <= 17) return `${hourNum - 12} PM`
  if (hourNum > 17) return null
  
  return null
}

const fallbackData = [
  { time: '7 AM', count: 6500 },
  { time: '8 AM', count: 8500 },
  { time: '9 AM', count: 6800 },
  { time: '10 AM', count: 4800 },
  { time: '11 AM', count: 5200 },
  { time: '12 PM', count: 4200 },
  { time: '1 PM', count: 4500 },
  { time: '2 PM', count: 6200 },
  { time: '3 PM', count: 8200 },
  { time: '4 PM', count: 6500 },
  { time: '5 PM', count: 2800 },
]

const MessageCountAgent = ({
  data = [],
  title = '',
  height = 250,
  strokeColor = '#6B8E23',
  showTitle = true,
  valueKey = 'sessions',
  tooltipLabel = 'Sessions',
  domain = [0, 'dataMax'],
  showGrid = true,
  margin = { top: 10, right: 20, left: 10, bottom: 20 },
  isAnalyticsOverview = false,
  showTotalCount = false 
}) => {
  const transformedData = transformHourlySessionsData(data, valueKey, isAnalyticsOverview)
  const chartData = transformedData.length > 0 ? transformedData : fallbackData
  
  const totalCount = data && typeof data.messagesCount === 'number' ? data.messagesCount : 0
  
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload
      const value = payload[0].value
      return (
        <div className="bg-indigo-600 text-white px-3 py-2 rounded-lg shadow-lg border-0">
          <p className="text-sm font-medium">{`${tooltipLabel}`}</p>
          <p className="text-lg font-bold">{value.toLocaleString()}</p>
        </div>
      )
    }
    return null
  }

  // Analytics Overview Layout
  if (isAnalyticsOverview) {
    return (
      <div className="w-full h-full flex flex-col">
        {/* Header with title and count in same line */}
        
        <div className="mb-2 px-4">
          <span className="text-sm text-gray-600 font-medium">
            {title || 'Message Count'}: <span className="font-bold">{totalCount.toLocaleString()}</span>
          </span>
        </div>
        
        {/* Chart container that fills remaining space */}
        <div className="flex-1 flex items-center justify-center min-h-0">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart 
              data={chartData} 
              margin={{ top: 5, right: 15, left: 10, bottom: 15 }}
            >
              <CartesianGrid 
                stroke="#d1d5db" 
                horizontal={true}
                vertical={true}
              />
              <XAxis 
                dataKey="time" 
                axisLine={{ stroke: '#374151', strokeWidth: 2 }} 
                tickLine={false}
                tick={{ fontSize: 10, fill: '#374151', fontWeight: 500 }}
                tickMargin={8}
                height={35}
              />
              <YAxis 
                type="number" 
                domain={[0, 'dataMax']}
                axisLine={{ stroke: '#374151', strokeWidth: 2 }} 
                tickLine={false}
                tick={{ fontSize: 10, fill: '#374151', fontWeight: 500 }}
                tickFormatter={(value) => {
                  if (value === 0) return '0'
                  if (value >= 1000) return `${Math.round(value / 1000)}k`
                  return value.toString()
                }}
                width={40}
              />
              <Tooltip content={<CustomTooltip />} />
              <Line
                type="linear"
                dataKey="count"
                stroke={strokeColor}
                strokeWidth={2}
                dot={false}
                activeDot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    )
  }

  // Default Layout (updated for centering)
  return (
    <div className="w-full h-full flex flex-col bg-white p-4 rounded-lg">
      {showTitle && title && (
        <div className="mb-2 ">
          <h3 className="text-sm font-medium text-gray-600 flex  gap-2">
            {title}
            {showTotalCount && (
              <span className="text-lg font-bold text-gray-900">{totalCount.toLocaleString()}</span>
            )}
          </h3>
        </div>
      )}
      
      <div className="flex-1 flex items-center justify-center">
        <ResponsiveContainer width="100%" height={height}>
          <LineChart data={chartData} margin={margin}>
            {showGrid && (
              <CartesianGrid 
                strokeDasharray="none" 
                stroke="#f3f4f6" 
                horizontal={true}
                vertical={true}
                strokeWidth={1}
              />
            )}
            <XAxis 
              dataKey="time" 
              axisLine={true} 
              tickLine={true}
              tick={{ fontSize: 10, fill: '#6b7280' }}
              tickMargin={10}
              height={35}
            />
            <YAxis 
              type="number" 
              domain={[0, 'dataMax']}
              axisLine={true} 
              tickLine={false}
              tick={{ fontSize: 10, fill: '#6b7280' }}
              tickFormatter={(value) => {
                if (value === 0) return '0'
                if (value >= 1000) return `${Math.round(value / 1000)}k`
                return value.toString()
              }}
              width={40}
            />
            <Tooltip content={<CustomTooltip />} />
            <Line
            type="linear"
            dataKey="volume"
            stroke="#6A5ACD"
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 6, strokeWidth: 2, fill: '#6A5ACD', stroke: '#fff' }}
          />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

export default MessageCountAgent

MessageCountAgent.propTypes = {
  data: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  title: PropTypes.string,
  height: PropTypes.number,
  strokeColor: PropTypes.string,
  showTitle: PropTypes.bool,
  valueKey: PropTypes.string,
  tooltipLabel: PropTypes.string,
  domain: PropTypes.array,
  showGrid: PropTypes.bool,
  margin: PropTypes.object,
  isAnalyticsOverview: PropTypes.bool,
  showTotalCount: PropTypes.bool,
}

MessageCountAgent.defaultProps = {
  data: [],
  title: '',
  height: 250,
  strokeColor: '#6B8E23',
  showTitle: true,
  valueKey: 'sessions',
  tooltipLabel: 'Sessions',
  domain: [0, 'dataMax'],
  showGrid: true,
  margin: { top: 10, right: 20, left: 10, bottom: 20 },
  isAnalyticsOverview: false,
  showTotalCount: false,
}